import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import { useTranslation } from 'react-i18next';
import orderService from '../services/orderService';
import productService from '../services/productService';
import { deliveryService } from '../services/deliveryService'; // Import deliveryService
import type { Product, CreateOrderPayload } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { GhostSelect } from './ui/GhostSelect';
import { useNotifications } from './ui/notifications';

interface OrderFormProps {
  onOrderCreated: (orderId: number, total: number, paymentLink?: string) => void;
  onClose: () => void;
}

interface OrderItem {
  producto_id: number;
  cantidad: number;
  nombre?: string; // For display purposes
  precio?: number; // For display purposes
}

interface NominatimSuggestion {
  display_name: string;
  lat: string;
  lon: string;
}

function OrderForm({ onOrderCreated, onClose }: OrderFormProps) {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const user = useAppSelector((s) => s.auth.user);
  const [nombreCliente, setNombreCliente] = useState('');
  const [telefonoCliente, setTelefonoCliente] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [tipoPedido, setTipoPedido] = useState<'domicilio' | 'recogida'>('domicilio');
  const [canalComunicacion, setCanalComunicacion] = useState('');
  const [metodoPago, setMetodoPago] = useState('');
  const [direccionEntregaInput, setDireccionEntregaInput] = useState(''); // Input for address
  const [addressSuggestions, setAddressSuggestions] = useState<NominatimSuggestion[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<{
    fullAddress: string;
    lat: number;
    lon: number;
  } | null>(null);
  const [isAddressDeliverable, setIsAddressDeliverable] = useState<boolean | null>(null);
  const [addressVerificationLoading, setAddressVerificationLoading] = useState(false);
  const [addressVerificationError, setAddressVerificationError] = useState<string | null>(null);
  const [notasCliente, setNotasCliente] = useState('');
  const [codigoDescuento, setCodigoDescuento] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<OrderItem[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [productSelectionError, setProductSelectionError] = useState<string | null>(null);

  // Debounce for address autocompletion
  useEffect(() => {
    const handler = setTimeout(() => {
      if (direccionEntregaInput.length > 3 && tipoPedido === 'domicilio') {
        fetchAddressSuggestions(direccionEntregaInput);
      } else {
        setAddressSuggestions([]);
        setIsAddressDeliverable(null);
        setSelectedAddress(null);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [direccionEntregaInput, tipoPedido]);

  // Effect to verify address when selectedAddress changes
  useEffect(() => {
    if (selectedAddress && user?.comercioKey) {
      verifyAddressInZone(selectedAddress.fullAddress);
    } else {
      setIsAddressDeliverable(null);
      setAddressVerificationError(null);
    }
  }, [selectedAddress, user?.comercioKey]);

  const fetchAddressSuggestions = async (query: string) => {
    setAddressVerificationLoading(true);
    setAddressVerificationError(null);
    try {
      // Using Nominatim public API for autocompletion
      const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=es`);
      const data: NominatimSuggestion[] = await response.json();
      setAddressSuggestions(data);
    } catch (err) {
      console.error('Error fetching address suggestions:', err);
      setAddressVerificationError('Error fetching address suggestions.');
    } finally {
      setAddressVerificationLoading(false);
    }
  };

  const verifyAddressInZone = async (address: string) => {
    if (!user?.comercioKey) return;

    setAddressVerificationLoading(true);
    setAddressVerificationError(null);
    try {
      const result = await deliveryService.verifyDeliveryAddress(user.comercioKey, address);
      setIsAddressDeliverable(result.isDeliverable);
    } catch (err) {
      console.error('Error verifying address:', err);
      setIsAddressDeliverable(false);
      setAddressVerificationError('Could not verify address with delivery zones.');
    } finally {
      setAddressVerificationLoading(false);
    }
  };

  const handleSelectAddress = (suggestion: NominatimSuggestion) => {
    setDireccionEntregaInput(suggestion.display_name);
    setSelectedAddress({
      fullAddress: suggestion.display_name,
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
    });
    setAddressSuggestions([]); // Clear suggestions
  };

  useEffect(() => {
    const fetchProducts = async () => {
      if (user && user.comercioKey) {
        try {
          const products = await productService.getProducts(user.comercioKey);
          setAvailableProducts(products);
        } catch (err) {
          console.error('Error fetching products:', err);
          setError('Error al cargar productos disponibles.');
        }
      }
    };
    fetchProducts();
  }, [user]);

  const handleProductChange = (productId: number, quantity: number) => {
    setSelectedProducts((prevProducts) => {
      const existingProductIndex = prevProducts.findIndex(item => item.producto_id === productId);

      if (quantity <= 0) {
        // Remove product if quantity is 0 or less
        return prevProducts.filter(item => item.producto_id !== productId);
      }

      if (existingProductIndex > -1) {
        // Update quantity of existing product
        const updatedProducts = [...prevProducts];
        updatedProducts[existingProductIndex] = { ...updatedProducts[existingProductIndex], cantidad: quantity };
        return updatedProducts;
      } else {
        // Add new product
        const product = availableProducts.find(p => p.id === productId);
        return [...prevProducts, { producto_id: productId, cantidad: quantity, nombre: product?.nombre, precio: product?.precio }];
      }
    });
  };

  const calculateTotal = () => {
    return selectedProducts.reduce((sum, item) => {
      const product = availableProducts.find(p => p.id === item.producto_id);
      return sum + (product ? product.precio * item.cantidad : 0);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setDiscountError(null);
    setProductSelectionError(null);
    setAddressVerificationError(null);
    setLoading(true);

    if (!user || !user.comercioKey) {
      setError('No se ha encontrado la clave de comercio. Por favor, inicia sesión.');
      setLoading(false);
      return;
    }

    if (selectedProducts.length === 0) {
      setProductSelectionError('Debe seleccionar al menos un producto.');
      setLoading(false);
      return;
    }

    let finalDireccionEntrega = undefined;
    let finalLat = undefined;
    let finalLon = undefined;

    if (tipoPedido === 'domicilio') {
      if (!selectedAddress) {
        setError('Por favor, selecciona una dirección de entrega de la lista de sugerencias.');
        setLoading(false);
        return;
      }
      if (isAddressDeliverable === false) {
        setError('La dirección seleccionada no está dentro de la zona de entrega del comercio.');
        setLoading(false);
        return;
      }
      finalDireccionEntrega = selectedAddress.fullAddress;
      finalLat = selectedAddress.lat;
      finalLon = selectedAddress.lon;
    }

    try {
      const orderData: CreateOrderPayload = {
        nombre_cliente: nombreCliente,
        telefono_cliente: telefonoCliente,
        email_cliente: emailCliente || undefined,
        tipo: tipoPedido,
        canal: canalComunicacion || undefined,
        metodo_pago: metodoPago || undefined,
        direccion_entrega: finalDireccionEntrega,
        direccion_entrega_lat: finalLat,
        direccion_entrega_lon: finalLon,
        notas_cliente: notasCliente || undefined,
        items: selectedProducts.map(item => ({
          producto_id: item.producto_id,
          cantidad: item.cantidad,
        })),
        codigo_descuento: codigoDescuento || undefined,
      };

      const response = await orderService.createOrder(user.comercioKey, orderData);
      setSuccessMessage('Pedido creado exitosamente!');
      notifications.success('Pedido creado', `El pedido de ${nombreCliente.trim()} se añadió correctamente.`);
      onOrderCreated(response.pedido_id, response.total, response.paymentLink);
      // Optionally clear form or close modal
      setNombreCliente('');
      setTelefonoCliente('');
      setEmailCliente('');
      setTipoPedido('domicilio');
      setCanalComunicacion('');
      setMetodoPago('');
      setDireccionEntregaInput('');
      setAddressSuggestions([]);
      setSelectedAddress(null);
      setIsAddressDeliverable(null);
      setAddressVerificationLoading(false);
      setAddressVerificationError(null);
      setNotasCliente('');
      setCodigoDescuento('');
      setSelectedProducts([]);
    } catch (err: any) {
      console.error('Error creating order:', err);
      const serverError = err.response?.data?.error;
      if (serverError && serverError.includes('Código de descuento inválido')) {
        setDiscountError('Código de descuento inválido o no disponible.');
        notifications.warning('Revisa el descuento', 'El código de descuento no es válido o no está disponible.');
      } else {
        const message = err.response?.data?.message || 'Error al crear el pedido.';
        setError(message);
        notifications.error('No se pudo crear el pedido', message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4 sm:p-0">
      <div className="bg-black/90 p-6 sm:p-8 rounded-3xl shadow-2xl border border-white/20 max-w-full sm:max-w-2xl w-full relative max-h-[90vh] overflow-y-auto dark-bg overflow-x-hidden">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/60 hover:text-white hover:text-white/80 text-2xl font-bold"
        >
          &times;
        </button>
        <h2 className="text-2xl font-bold mb-6 text-white">{t('ordersPage.createNew')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Details */}
          <div>
            <h3 className="text-xl font-semibold mb-3 text-white/80">Detalles del Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nombreCliente" className="block text-sm font-medium text-white/80">Nombre del Cliente *</label>
                <input
                  type="text"
                  id="nombreCliente"
                  className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={nombreCliente}
                  onChange={(e) => setNombreCliente(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="telefonoCliente" className="block text-sm font-medium text-white/80">Teléfono del Cliente *</label>
                <input
                  type="text"
                  id="telefonoCliente"
                  className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={telefonoCliente}
                  onChange={(e) => setTelefonoCliente(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="emailCliente" className="block text-sm font-medium text-white/80">Email del Cliente</label>
                <input
                  type="email"
                  id="emailCliente"
                  className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={emailCliente}
                  onChange={(e) => setEmailCliente(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="canalComunicacion" className="block text-sm font-medium text-white/80">Canal de Comunicación</label>
                <GhostSelect
                  value={canalComunicacion}
                  onChange={(val: string) => setCanalComunicacion(val)}
                  className="w-full mt-1"
                  options={[
                    { value: "", label: "Selecciona un canal" },
                    { value: "ManyChat", label: "ManyChat" },
                    { value: "WhatsApp", label: "WhatsApp" },
                    { value: "Llamada Telefonica", label: "Llamada Telefonica" },
                    { value: "Web", label: "Web" },
                    { value: "Presencial", label: "Presencial" }
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Order Type and Address */}
          <div>
            <h3 className="text-xl font-semibold mb-3 text-white/80">Tipo de Pedido</h3>
            <div className="flex items-center space-x-4 mb-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="tipoPedido"
                  value="domicilio"
                  checked={tipoPedido === 'domicilio'}
                  onChange={() => setTipoPedido('domicilio')}
                />
                <span className="ml-2 text-white/80">A Domicilio</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="tipoPedido"
                  value="recogida"
                  checked={tipoPedido === 'recogida'}
                  onChange={() => setTipoPedido('recogida')}
                />
                <span className="ml-2 text-white/80">Recogida Local</span>
              </label>
            </div>
            {tipoPedido === 'domicilio' && (
              <div>
                <label htmlFor="direccionEntregaInput" className="block text-sm font-medium text-white/80">Dirección de Entrega *</label>
                <input
                  type="text"
                  id="direccionEntregaInput"
                  className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  value={direccionEntregaInput}
                  onChange={(e) => {
                    setDireccionEntregaInput(e.target.value);
                    setSelectedAddress(null); // Clear selected address on input change
                    setIsAddressDeliverable(null); // Clear verification status
                  }}
                  placeholder="Empieza a escribir una dirección..."
                />
                {addressVerificationLoading && (
                  <p className="text-blue-500 text-sm mt-1 flex items-center">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Verificando dirección...
                  </p>
                )}
                {isAddressDeliverable === true && (
                  <p className="text-green-600 text-sm mt-1 flex items-center">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> Dirección dentro de la zona de entrega.
                  </p>
                )}
                {isAddressDeliverable === false && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-2" /> Dirección FUERA de la zona de entrega.
                  </p>
                )}
                {addressVerificationError && (
                  <p className="text-red-500 text-sm mt-1">Error: {addressVerificationError}</p>
                )}

                {addressSuggestions.length > 0 && (
                  <ul className="border border-white/20 rounded-md mt-1 max-h-48 overflow-y-auto bg-black/90 shadow-2xl border-white/20 text-white z-10 relative">
                    {addressSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="p-2 cursor-pointer hover:bg-white/10"
                        onClick={() => handleSelectAddress(suggestion)}
                      >
                        {suggestion.display_name}
                      </li>
                    ))}
                  </ul>
                )}
                {selectedAddress && (
                  <p className="text-white/70 text-sm mt-2">
                    Dirección seleccionada: <span className="font-semibold">{selectedAddress.fullAddress}</span>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Products Selection */}
          <div>
            <h3 className="text-xl font-semibold mb-3 text-white/80">Productos</h3>
            {productSelectionError && <p className="text-red-500 text-sm mb-2">{productSelectionError}</p>}
            <div className="space-y-2">
              {availableProducts.length === 0 && !loading && <p className="text-white/70">No hay productos disponibles.</p>}
              {availableProducts.map((product) => (
                <div key={product.id} className="flex items-center justify-between bg-black/20 border border-white/10 p-3 rounded-md">
                  <span className="text-white font-medium">{product.nombre} - {typeof product.precio === 'number' ? product.precio.toFixed(2) : parseFloat(product.precio || '0').toFixed(2)}€</span>
                  <input
                    type="number"
                    min="0"
                    className="w-20 border border-white/20 bg-black/75 text-white rounded-md shadow-sm p-1 text-center focus:outline-none focus:ring-green-500"
                    value={selectedProducts.find(item => item.producto_id === product.id)?.cantidad || ''}
                    onChange={(e) => handleProductChange(product.id, parseInt(e.target.value))}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 text-right text-lg font-bold text-white">
              Total Provisional: {calculateTotal().toFixed(2)}€
            </div>
          </div>

          {/* Payment Method and Notes */}
          <div>
            <h3 className="text-xl font-semibold mb-3 text-white/80">Información Adicional</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="metodoPago" className="block text-sm font-medium text-white/80">Método de Pago</label>
                <GhostSelect
                  value={metodoPago}
                  onChange={(val: string) => setMetodoPago(val)}
                  className="w-full mt-1"
                  options={[
                    { value: "", label: "Selecciona un método" },
                    { value: "Efectivo", label: "Efectivo" },
                    { value: "Tarjeta", label: "Tarjeta" },
                    { value: "Transferencia", label: "Transferencia" },
                    { value: "Stripe", label: "Stripe" },
                    { value: "PayPal", label: "PayPal" },
                    { value: "Otro", label: "Otro" }
                  ]}
                />
              </div>
              <div>
                <label htmlFor="codigoDescuento" className="block text-sm font-medium text-white/80">Código de Descuento</label>
                <input
                  type="text"
                  id="codigoDescuento"
                  className={`mt-1 block w-full border ${discountError ? 'border-red-500' : 'border-white/20'} bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500`}
                  value={codigoDescuento}
                  onChange={(e) => {
                    setCodigoDescuento(e.target.value);
                    setDiscountError(null); // Clear error when user types
                  }}
                />
                {discountError && <p className="text-red-500 text-xs mt-1">{discountError}</p>}
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="notasCliente" className="block text-sm font-medium text-white/80">Notas para el Pedido</label>
              <textarea
                id="notasCliente"
                rows={3}
                className="mt-1 block w-full border border-white/20 bg-black/20 text-white placeholder-white/50 focus:bg-black rounded-md shadow-sm p-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                value={notasCliente}
                onChange={(e) => setNotasCliente(e.target.value)}
              ></textarea>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
          {successMessage && <p className="text-green-500 text-sm">{successMessage}</p>}

          <button
            type="submit"
            className="btn-taratic btn-blue w-full font-bold py-2 px-4 rounded-md transition duration-300"
            disabled={loading}
          >
            {loading ? 'Creando Pedido...' : 'Crear Pedido'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OrderForm;
